import * as React from "react";
import { Button, Stack, Typography, Box } from "@mui/material";
import { CustomTable, CustomeDialog, DeleteModal } from "../../../shared";
import { sortItems } from "../../../utils";
import AddIcon from "@mui/icons-material/Add";
import StoreIcon from '@mui/icons-material/Store';
import { useParams } from "react-router-dom";
import { usePointPromotionStores } from "../hooks";
import { CategoryStoresDialog } from "./CategoryStoresDialog";

export const PointPromotionStoresTab = ({ data }) => {
  const { id } = useParams();
  const {
    state,
    openAssignModal,
    closeAssignModal,
    HandleFilterStore,
    handleStoreSelect,
    handleAssignStores,
    handleSelectAllStores,
    DeleteModals,
    handleDeleteStore,
    applyToAllStores,
    handleApplyAllConfirmModal,
  } = usePointPromotionStores({
    promotionId: id,
    promotionData: data,
  });

  const ActionButtons = ({ row, deleteAction }) => {
    return (
      <div className="action-buttons" onClick={(e) => e.preventDefault()}>
        <img
          alt="delete"
          onClick={deleteAction}
          src="/icons/ic_delete.svg"
          style={{
            cursor: "pointer",
            opacity: 1,
          }}
        />
      </div>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Store Name",
        accessor: "name",
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, columnId);
        },
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        disableGlobalFilter: true,
        accessor: (row) => {
          return (
            <ActionButtons
              row={row}
              deleteAction={() => {
                DeleteModals(true, row.id);
              }}
            />
          );
        },
      },
    ],
    [],
  );

  const selectedStore = state.assign.storeList?.find(
    (item) => item.id === state.storeId,
  );
  const isLastSelectedStore =
    (state.assign.storeList?.length || 0) === 1 && !!selectedStore;
  const deleteModalDescription = isLastSelectedStore ? (
    <>
      This is the last selected store. After deleting, this will no longer be
      limited to specific stores and will apply to all stores.
      <br />
      <br />
      {`Are you sure you want to Delete the Store "${selectedStore?.name}" ?`}
    </>
  ) : (
    "Are you sure you want to remove this store from the promotion?"
  );

  return (
    <>
      {state.assign.storeList?.length ? (
        <CustomTable
          columns={columns}
          data={state.assign.storeList || []}
          isLoading={state.isBusy}
          searchBar={true}
          defaultSearch={true}
          tableTitle="Stores"
          bulkDelete={false}
          emptyMessage="No stores found"
          headerRightContent={
            <Stack direction="row" spacing={2}>
              <Button
                type="button"
                size="medium"
                variant="text"
                onClick={() => handleApplyAllConfirmModal(true)}
              >
                Apply To All Stores
              </Button>
              <Button
                type="button"
                size="medium"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAssignModal}
              >
                Add/Modify Stores
              </Button>
            </Stack>
          }
        />
      ) : (
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            border: "2px Dashed #D1D5DC",
            borderRadius: "12px",
            padding: "16px",
            my: "32px",
          }}
        >
          <Stack
            sx={{
              borderRadius: "50px",
              minWidth: "40px",
              height: "40px",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#E0E7FF",
            }}
          >
            <StoreIcon color="primary" />
          </Stack>
          <Stack sx={{ width: "100%", gap: "4px" }}>
            <Typography
              fontWeight="bold"
              fontSize="18px"
              lineHeight="20px"
              color="#111111"
            >
              Available to all Stores
            </Typography>
            <Typography fontWeight={400} fontSize="14px" color="#636363">
              This promotion is available to all stores. You can restrict access
              by selecting specific stores.
            </Typography>
          </Stack>
          <Button
            type="button"
            size="medium"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ minWidth: "220px" }}
            onClick={openAssignModal}
          >
            Add Stores
          </Button>
        </Stack>
      )}

      <DeleteModal
        open={state.isDelete}
        handleClose={() => DeleteModals(false)}
        onConfirm={handleDeleteStore}
        isDeleteButton={state.isBusy}
        title="Delete Store"
        description={deleteModalDescription}
      />

      <CustomeDialog
        open={state.isApplyAllConfirmOpen}
        PaperProps={{
          sx: { width: "423px", p: "16px" },
        }}
        title="Apply To All Stores"
        content={
          <Stack gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This will make this promotion applicable to all stores. Are you
              sure you want to continue?
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                type="button"
                size="medium"
                onClick={() => handleApplyAllConfirmModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="medium"
                variant="contained"
                onClick={applyToAllStores}
                disabled={state.isBusy}
              >
                Confirm
              </Button>
            </Stack>
          </Stack>
        }
      />

      <CustomeDialog
        open={state.isOpen}
        handleClose={closeAssignModal}
        aria-labelledby="point-promotion-stores-dialog"
        aria-describedby="point-promotion-stores-dialog-content"
        PaperProps={{
          sx: { width: "423px", height: "535px", p: "16px" },
        }}
        title="Stores"
        content={
          <CategoryStoresDialog
            Items={state.assign.filteredStores}
            onSearch={HandleFilterStore}
            onSelect={handleStoreSelect}
            selectedItems={state.assign.selectedStores}
            existedStoreList={state.assign.storeList.map((s) => s.id)}
            onSubmit={handleAssignStores}
            disabled={state.assign.isSaveButtonBusy}
            type="modify"
            isLoading={state.assign.isModalLoading}
            isButtonLoading={state.assign.isSaveButtonBusy}
            onSelectAll={handleSelectAllStores}
          />
        }
      />
    </>
  );
};
